import { lazy } from 'react';
import { ADMIN } from '@noissue/authz';
import { ORGCHART } from '@noissue/orgchart/permissions';
import { PIM } from '@noissue/pim-poc/permissions';
import { QUOTING } from '@noissue/quoting/permissions';
import { ROADMAP } from '@noissue/roadmap/permissions';
import { PRODUCT_ROADMAP } from '@noissue/product-roadmap/permissions';
import { PLATFORM_ROADMAP } from '@noissue/platform-roadmap/permissions';
import { CULTURE_ROADMAP } from '@noissue/culture-roadmap/permissions';
import { PRICING } from '@noissue/pricing/permissions';
import { SUPPORT } from '@noissue/support/permissions';
import {
  AdminPage,
  GroupDetailPage,
  UsersPage,
  UserAccessPage,
  type RegisteredModule,
} from './shell';

/**
 * Every module's permission spec, for the access-control UI to render levels and
 * permission labels. Mirrors the `moduleEnvs` pattern in apps/server/src/env-spec.ts:
 * the host lists the specs because it is the only thing that imports every module.
 *
 * apps/server keeps its own list (for seeding and the admin API) since apps cannot
 * import each other. That duplicates the LIST, not any metadata — name and icon now
 * come from the spec, which removed the previous three-way copy between seed.ts, this
 * file and the database row.
 */
export const moduleSpecs = [
  QUOTING,
  PRICING,
  ORGCHART,
  ROADMAP,
  PRODUCT_ROADMAP,
  PLATFORM_ROADMAP,
  CULTURE_ROADMAP,
  PIM,
  SUPPORT,
  ADMIN,
];

/**
 * The static module registry (Phase 1). This is the one place modules are wired
 * into the app; apps/web is the only package that imports each module. The
 * `requiredPermissions` mirror each module's DB row so the nav can hide entries,
 * but the server independently guards every module API route.
 *
 * Quoting is lazily imported (route-based code splitting, ADR §1). Admin ships
 * with the shell. Adding PIM later is a one-line entry here plus its package.
 */
const QuotingPage = lazy(() =>
  import('@noissue/quoting/web').then((m) => ({ default: m.QuotingPage })),
);
const QuoteBuilderPage = lazy(() =>
  import('@noissue/quoting/web').then((m) => ({ default: m.QuoteBuilderPage })),
);
const CatalogPage = lazy(() =>
  import('@noissue/quoting/web').then((m) => ({ default: m.CatalogPage })),
);
const CatalogProductPage = lazy(() =>
  import('@noissue/quoting/web').then((m) => ({ default: m.CatalogProductPage })),
);
const PricingSettingsPage = lazy(() =>
  import('@noissue/pricing/web').then((m) => ({ default: m.PricingSettingsPage })),
);
const RfqsPage = lazy(() => import('@noissue/quoting/web').then((m) => ({ default: m.RfqsPage })));
const RfqPage = lazy(() => import('@noissue/quoting/web').then((m) => ({ default: m.RfqPage })));
const ApprovalsPage = lazy(() =>
  import('@noissue/quoting/web').then((m) => ({ default: m.ApprovalsPage })),
);
const SupplierRfqPage = lazy(() =>
  import('@noissue/quoting/web').then((m) => ({ default: m.SupplierRfqPage })),
);
const OrgChartPage = lazy(() =>
  import('@noissue/orgchart/web').then((m) => ({ default: m.OrgChartPage })),
);
const PlannerPage = lazy(() =>
  import('@noissue/roadmap/web').then((m) => ({ default: m.PlannerPage })),
);
const ProductRoadmapPage = lazy(() =>
  import('@noissue/product-roadmap/web').then((m) => ({ default: m.ProductRoadmapPage })),
);
const PlatformRoadmapPage = lazy(() =>
  import('@noissue/platform-roadmap/web').then((m) => ({ default: m.PlatformRoadmapPage })),
);
const CultureRoadmapPage = lazy(() =>
  import('@noissue/culture-roadmap/web').then((m) => ({ default: m.CultureRoadmapPage })),
);
// Seven handles on ONE split point: every specifier is '@noissue/pim-poc/web', so these are
// seven lazy references to a single chunk, not five chunks.
const PimCategoriesPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimCategoriesPage })),
);
const PimAttributesPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimAttributesPage })),
);
const PimProductsPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimProductsPage })),
);
const PimProductDetailPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimProductDetailPage })),
);
const PimProductFormPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimProductFormPage })),
);
const PimVariantsPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimVariantsPage })),
);
const PimVariantFormPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimVariantFormPage })),
);
const PimVariantDetailPage = lazy(() =>
  import('@noissue/pim-poc/web').then((m) => ({ default: m.PimVariantDetailPage })),
);
const DeleteCustomerDataPage = lazy(() =>
  import('@noissue/support/web').then((m) => ({ default: m.DeleteCustomerDataPage })),
);

export const modules: RegisteredModule[] = [
  {
    id: QUOTING.id,
    name: QUOTING.name,
    requiredPermissions: [QUOTING.visibilityPermission],
    // Section items. Catalog paths sit under /quoting/* so a future PIM module can own
    // its own /pim/catalog without colliding (Quoting → Catalog vs PIM → Catalog).
    navItems: [
      { label: 'Quotes', icon: 'calculator', path: '/quoting', Component: QuotingPage },
      // RFQs of every kind (quoting build plan §12). Visible to every quoting user, and useful
      // to all: a team sees its kind's queue, and
      // everyone sees the RFQs they raised. The server's guard decides which queues load.
      { label: 'RFQs', icon: 'inbox', path: '/quoting/rfqs', Component: RfqsPage },
      // Margin sign-off: only for holders of the off-ladder `approve` grant.
      {
        label: 'Approvals',
        icon: 'stamp',
        path: '/quoting/approvals',
        Component: ApprovalsPage,
        requiredPermissions: [QUOTING.p.approve],
      },
      { label: 'Catalog', icon: 'package', path: '/quoting/catalog', Component: CatalogPage },
    ],
    // Non-nav owned routes: the builder (by id) and the catalog product detail.
    routes: [
      // The builder's active step is in the URL (/quoting/:id/customer), so tab
      // switches are history entries and Back walks back a step rather than
      // leaving the quote. The bare /quoting/:id is kept because links to a quote
      // shouldn't have to know its first step — the builder redirects it to the
      // first step of the quote type once loaded.
      { path: '/quoting/$quoteId', Component: QuoteBuilderPage },
      { path: '/quoting/$quoteId/$stepId', Component: QuoteBuilderPage },
      { path: '/quoting/catalog/$productId', Component: CatalogProductPage },
      // One RFQ's brief: the link a rep copies into the answering team's chat.
      { path: '/quoting/rfqs/$rfqId', Component: RfqPage },
      { path: '/quoting/approvals/$approvalId', Component: ApprovalsPage },
    ],
    // A supplier's emailed RFQ link — no login; the token in the URL is the credential.
    publicRoutes: [{ path: '/supplier/rfq/$token', Component: SupplierRfqPage }],
  },
  {
    // Pricing: its own module and section. Administrators only today (they get every module's
    // highest level); the settings page is read-only and its editing will need
    // `pricing:manage`.
    id: PRICING.id,
    name: PRICING.name,
    requiredPermissions: [PRICING.visibilityPermission],
    navItems: [
      {
        label: 'Settings',
        icon: PRICING.icon,
        path: '/pricing/settings',
        Component: PricingSettingsPage,
      },
    ],
  },
  {
    // NOT granted to All Users, unlike the Org Chart below — access is given per group in
    // the Admin section. Everyone who gets it gets Edit in practice; the View rung exists
    // so a read-only share to non-leaders is a grant rather than a rebuild.
    id: ROADMAP.id,
    name: ROADMAP.name,
    // Under Company, beside the org chart: both are company-wide views, and the planner
    // is one page — a section of its own for a single item was heading-per-page.
    section: 'Company',
    requiredPermissions: [ROADMAP.visibilityPermission],
    // One page. The win detail is a dialog driven by a `?win=` search param rather than a
    // route, so opening a card is shareable without the grid unmounting behind it.
    // `full`: the planner is one continuous grid across five years, so its value IS its
    // horizontal extent. Framed in the shared 80rem column it wastes most of a wide
    // monitor and forces horizontal scrolling that the screen could have absorbed.
    navItems: [
      {
        label: 'Company Wall Planner',
        icon: 'calendar-range',
        path: '/roadmap',
        Component: PlannerPage,
        layout: 'full',
      },
    ],
  },
  // The three team roadmaps: what each team is shipping when, aligned to the wall planner's
  // targets. Each is its own module with its own grant — the product team edits the product
  // roadmap without being handed the leadership planner — and all three file under Company
  // beside it. `full` for the same reason as the planner: their value is horizontal extent.
  {
    id: PRODUCT_ROADMAP.id,
    name: PRODUCT_ROADMAP.name,
    section: 'Company',
    requiredPermissions: [PRODUCT_ROADMAP.visibilityPermission],
    // The open product (`?product=`) and the exec/banked/archive views (`?view=`) are search
    // params, not routes, so the board never unmounts behind the dialog.
    navItems: [
      {
        label: 'Product Roadmap',
        icon: 'package-check',
        path: '/product-roadmap',
        Component: ProductRoadmapPage,
        layout: 'full',
      },
    ],
  },
  {
    id: PLATFORM_ROADMAP.id,
    name: PLATFORM_ROADMAP.name,
    section: 'Company',
    requiredPermissions: [PLATFORM_ROADMAP.visibilityPermission],
    navItems: [
      {
        label: 'Platform Roadmap',
        icon: 'rocket',
        path: '/platform-roadmap',
        Component: PlatformRoadmapPage,
        layout: 'full',
      },
    ],
  },
  {
    id: CULTURE_ROADMAP.id,
    name: CULTURE_ROADMAP.name,
    section: 'Company',
    requiredPermissions: [CULTURE_ROADMAP.visibilityPermission],
    navItems: [
      {
        label: 'Culture Roadmap',
        icon: 'party-popper',
        path: '/culture-roadmap',
        Component: CultureRoadmapPage,
        layout: 'full',
      },
    ],
  },
  {
    // Visible to everyone: `orgchart:view` is granted to the All Users group rather
    // than per user (apps/server/src/platform/groups.ts), so this needs no special
    // casing here — the server resolves it like any other grant.
    id: ORGCHART.id,
    name: ORGCHART.name,
    // Company-wide pages share one heading rather than getting a section each — see
    // `section` in shell/types.ts for why several modules can name the same section without
    // conflicting.
    section: 'Company',
    requiredPermissions: [ORGCHART.visibilityPermission],
    navItems: [{ label: 'Org chart', icon: 'network', path: '/orgchart', Component: OrgChartPage }],
  },
  {
    // PIM owns the whole /pim/* prefix. Quoting's catalogue was deliberately put at
    // /quoting/catalog (see its entry above) so this could exist without colliding —
    // there is no registry-level collision check, ownership is by convention only.
    //
    // Its own section, not merged into Company or Quoting: two nav items under one
    // heading is what `section` defaults to anyway (the module's `name`), and filing
    // product data under Quoting would imply an ownership that runs the wrong way.
    id: PIM.id,
    name: PIM.name,
    requiredPermissions: [PIM.visibilityPermission],
    navItems: [
      // `contained`: four columns sit comfortably in the shared 80rem column, and
      // 40 rows never need more.
      { label: 'Products', icon: 'boxes', path: '/pim/products', Component: PimProductsPage },
      // `full`, flipped in Phase 2 now that there is a table to justify it. Seven
      // columns over 16,165 rows: the value IS the horizontal extent, and framed in
      // 80rem the SKU, dimensions and supplier columns all truncate at once. The
      // page takes on its half of the bargain — AppLayout's <main> is
      // overflow-hidden, so a `full` route that does not scroll internally clips —
      // by pinning its control panel and pager and scrolling only the table.
      {
        label: 'Variants',
        icon: 'layers',
        path: '/pim/variants',
        Component: PimVariantsPage,
        layout: 'full',
      },
      // Last two: reference data the others are filed against, not what anyone
      // opens first.
      {
        label: 'Categories',
        icon: 'folder-tree',
        path: '/pim/categories',
        Component: PimCategoriesPage,
      },
      {
        label: 'Attributes',
        icon: 'tags',
        path: '/pim/attributes',
        Component: PimAttributesPage,
      },
    ],
    // Detail views, reachable only by clicking a row, so non-nav routes. Keyed by
    // SKU rather than by id: the SKU is the identifier the API publishes and the one
    // a person can read back off a link.
    routes: [
      // `/new` before `/$sku`: the router matches in order, and a param route
      // would otherwise swallow it as a product whose SKU is "new".
      { path: '/pim/products/new', Component: PimProductFormPage },
      { path: '/pim/products/$sku/edit', Component: PimProductFormPage },
      { path: '/pim/products/$sku', Component: PimProductDetailPage },
      { path: '/pim/variants/new', Component: PimVariantFormPage },
      { path: '/pim/variants/$sku/edit', Component: PimVariantFormPage },
      { path: '/pim/variants/$sku', Component: PimVariantDetailPage },
    ],
  },
  {
    // A container for support tooling rather than one app: "Customer Support" is the
    // sidebar heading and today it has a single entry under it. The nav items are the
    // TOOLS, which is why the module's own name never appears as a link — the next
    // tool is one more entry here, not a new section.
    //
    // Not granted to All Users: every tool in here reaches customer PII, and the first
    // one deletes it. Access is given per group in the Admin section, and the erase
    // rung is a separate grant above merely having the module.
    id: SUPPORT.id,
    name: SUPPORT.name,
    requiredPermissions: [SUPPORT.visibilityPermission],
    navItems: [
      {
        label: 'Delete customer data',
        icon: 'user-x',
        path: '/support/delete-customer-data',
        Component: DeleteCustomerDataPage,
      },
    ],
  },
  {
    id: ADMIN.id,
    name: ADMIN.name,
    requiredPermissions: [ADMIN.visibilityPermission],
    navItems: [
      { label: 'Access control', icon: 'shield', path: '/admin', Component: AdminPage },
      { label: 'Users', icon: 'users', path: '/admin/users', Component: UsersPage },
    ],
    // Detail views: a group's members + access, and one person's effective access.
    // Both are reachable only from the pages above, so they are non-nav routes.
    routes: [
      { path: '/admin/groups/$groupId', Component: GroupDetailPage },
      { path: '/admin/users/$userId', Component: UserAccessPage },
    ],
  },
];
